import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../components/PageHeader'
import {
  Button,
  Chip,
  EmptyState,
  Input,
  Modal,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../components/ui'
import { useEdgeGateways, useEdgeInstances } from '../api/hooks'
import { api } from '../api/client'
import type { EdgeGateway } from '../api/types'
import { useAuthStore } from '../stores/auth'
import { relativeTime } from '../lib/format'
import { parseAllowedIpsInput } from '../lib/ipacl'
import { EnrollWizard } from '../components/EnrollWizard'
import { AllowedIpsField } from '../components/AllowedIpsField'

/** Connected/disconnected dot, matching the Systems list's status-dot pattern. */
export function ConnDot({ connected, className = '' }: { connected: boolean; className?: string }) {
  return (
    <span
      title={connected ? 'Connected' : 'Disconnected'}
      className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${connected ? 'bg-ostrel-ok' : 'bg-ostrel-danger'} ${className}`}
    />
  )
}

/**
 * Two-phase create modal: name + optional source-IP allowlist form → the
 * enrollment wizard (artifact choice → download + docs → gated token
 * reveal). The wizard mints the gateway (POST /gateways) only once an
 * available artifact is picked.
 */
function CreateGatewayModal({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: () => void
}) {
  const [name, setName] = useState('')
  const [aclText, setAclText] = useState('')
  const [enrolling, setEnrolling] = useState(false)

  const acl = useMemo(() => parseAllowedIpsInput(aclText), [aclText])

  return (
    <Modal open title="Add gateway" onClose={onClose}>
      {!enrolling ? (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (name.trim() && !acl.error) setEnrolling(true)
          }}
        >
          <p className="text-xs leading-relaxed text-ostrel-ink-muted">
            Provisions a gateway appliance slot for this customer and mints a one-time enroll
            token for it.
          </p>
          <Input
            label="Gateway name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Stockholm HQ appliance"
            required
          />
          <AllowedIpsField value={aclText} onChange={setAclText} error={acl.error} />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="navy" size="sm" disabled={!name.trim() || !!acl.error}>
              Continue
            </Button>
          </div>
        </form>
      ) : (
        <EnrollWizard
          enroll={async () => {
            const result = await api.createGateway({ name: name.trim(), allowed_ips: acl.cidrs })
            onCreated()
            return result.enroll_token
          }}
          onDone={onClose}
        />
      )}
    </Modal>
  )
}

export default function GatewaysPage() {
  const navigate = useNavigate()
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()

  const gateways = useEdgeGateways()
  const instances = useEdgeInstances()
  const [createOpen, setCreateOpen] = useState(false)

  /** Instance count per gateway uid, for the INSTANCES column. */
  const instanceCounts = useMemo(() => {
    const map = new Map<string, number>()
    for (const inst of instances.data ?? []) {
      map.set(inst.gateway, (map.get(inst.gateway) ?? 0) + 1)
    }
    return map
  }, [instances.data])

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [scope] })

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        section="Edge"
        title="Gateways"
        action={<Button onClick={() => setCreateOpen(true)}>+ Add gateway</Button>}
      />

      {gateways.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      ) : gateways.isError ? (
        <EmptyState title="Could not load gateways" hint={gateways.error.message} />
      ) : gateways.data.length === 0 ? (
        <EmptyState
          title="No gateways provisioned"
          hint="A gateway appliance on the customer's network reaches LAN-bound systems that the cloud cannot."
          action={<Button onClick={() => setCreateOpen(true)}>+ Add gateway</Button>}
        />
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>Status</TH>
              <TH>Name</TH>
              <TH>Unique id</TH>
              <TH>Agent</TH>
              <TH>Last heartbeat</TH>
              <TH className="text-right">Instances</TH>
            </tr>
          </THead>
          <TBody>
            {gateways.data.map((g: EdgeGateway) => (
              <TR
                key={g.unique_id}
                className="cursor-pointer"
                onClick={() => navigate(`/gateways/${g.unique_id}`)}
              >
                <TD>
                  <span className="flex items-center gap-2">
                    <ConnDot connected={g.connected} />
                    <Chip
                      tone={g.connected ? 'ok' : 'danger'}
                      label={g.connected ? 'Connected' : 'Disconnected'}
                    />
                  </span>
                </TD>
                <TD className="text-sm font-medium text-ostrel-ink">{g.name}</TD>
                <TD className="tnum font-mono text-xs text-ostrel-ink-muted">{g.unique_id}</TD>
                <TD className="tnum text-xs text-ostrel-ink-muted">{g.agent_version || '—'}</TD>
                <TD className="tnum text-xs text-ostrel-ink-muted">
                  {g.last_heartbeat_at ? relativeTime(g.last_heartbeat_at) : 'never'}
                </TD>
                <TD className="tnum text-right text-xs text-ostrel-ink-muted">
                  {instanceCounts.get(g.unique_id) ?? 0}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      <p className="max-w-3xl text-xs leading-relaxed text-ostrel-ink-muted">
        Gateways are small on-prem appliances that hold one persistent connection to Ostrel Security. Edge
        instances (pollers and relays) run on them to reach systems the cloud cannot.
      </p>

      {createOpen && (
        <CreateGatewayModal onClose={() => setCreateOpen(false)} onCreated={invalidate} />
      )}
    </div>
  )
}
